import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/SingleLineText';
import DateInputCard from './ui/DateInputCard';
import LoadingLabel from './LoadingLabel';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import BottomSheet from './BottomSheet';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';

const pad2 = (n) => String(n).padStart(2, '0');
const takenValueFontSize = '1.1rem';
const takenLabelColor = '#52705A';
const takenValueColor = '#1F8A3D';
const localDate = (d = new Date()) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const dateLabel = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return localize('날짜 미선택');
    const [, month, day] = value.split('-').map(Number);
    return localize("{v0}월 {v1}일", { v0: month, v1: day });
};
const nowHHMM = () => {
    const d = new Date();
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const isValidHHMM = (t) => {
    if (!/^\d{2}:\d{2}$/.test(t || '')) return false;
    const [h, m] = t.split(':').map(Number);
    return h >= 0 && h < 24 && m >= 0 && m < 60;
};
const formatTime = (time) => {
    if (!isValidHHMM(time)) return time;
    const [hour, minute] = time.split(':').map(Number);
    const hourLabel = localize("{v0} {v1}시", { v0: hour < 12 ? localize('오전') : localize('오후'), v1: hour % 12 || 12 });
    return minute === 0 ? hourLabel : localize("{v0} {v1}분", { v0: hourLabel, v1: minute });
};
export default function EditTakenTimeSheet({
    open, mode = 'single', scheduledTime, scheduledDate, initialDate, targets = [], initialTime,
    action = 'edit', loadConfirmationWindow, onClose, onConfirm,
}) {
    const [newTime, setNewTime] = useState(initialTime || '00:00');
    const [newDate, setNewDate] = useState(initialDate || scheduledDate || localDate());
    const [today, setToday] = useState(localDate);
    const [clock, setClock] = useState(() => new Date());
    const [refresh, setRefresh] = useState(0);
    const [windowState, setWindowState] = useState(null);
    const loaderRef = useRef(loadConfirmationWindow);
    loaderRef.current = loadConfirmationWindow;
    const idsKey = JSON.stringify(targets.map(t => String(t.id)));
    const windowKey = `${idsKey}:${today}:${refresh}`;
    const submittingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const timeDisplayRef = useRef(null);
    const titleId = 'edit-taken-time-title';

    useLayoutEffect(() => {
        if (open) {
            setNewTime(initialTime || '00:00');
            setNewDate(initialDate || scheduledDate || localDate());
            setToday(localDate());
            submittingRef.current = false;
            setSubmitting(false);
        }
    }, [open, mode, action, initialTime, initialDate, scheduledDate]);

    useEffect(() => {
        if (!open) return undefined;
        let midnightTimer;
        const refreshDay = () => {
            const now = new Date();
            setToday(localDate(now));
            setClock(now);
            window.clearTimeout(midnightTimer);
            const nextMidnight = new Date(now);
            nextMidnight.setHours(24, 0, 0, 20);
            midnightTimer = window.setTimeout(refreshDay, nextMidnight.getTime() - now.getTime());
        };
        const refreshOnReturn = () => {
            if (document.visibilityState === 'hidden') return;
            refreshDay();
            setRefresh(value => value + 1);
        };
        refreshDay();
        let minuteTimer;
        const refreshMinute = () => {
            const now = new Date();
            setClock(now);
            minuteTimer = window.setTimeout(refreshMinute, 60000 - (now.getTime() % 60000) + 20);
        };
        refreshMinute();
        window.addEventListener('focus', refreshOnReturn);
        document.addEventListener('visibilitychange', refreshOnReturn);
        return () => {
            window.clearTimeout(midnightTimer);
            window.clearTimeout(minuteTimer);
            window.removeEventListener('focus', refreshOnReturn);
            document.removeEventListener('visibilitychange', refreshOnReturn);
        };
    }, [open]);

    const hasWindowLoader = typeof loadConfirmationWindow === 'function';
    useEffect(() => {
        if (!open || !hasWindowLoader) return undefined;
        let active = true;
        setWindowState({ key: windowKey, loading: true });
        Promise.resolve().then(() => loaderRef.current(JSON.parse(idsKey))).then(data => {
            if (!data?.minDate || (data.before && Number.isNaN(new Date(data.before).getTime()))) throw new Error('Invalid window');
            if (active) setWindowState({ key: windowKey, data });
        }).catch(() => {
            if (active) setWindowState({ key: windowKey, error: true });
        });
        return () => { active = false; };
    }, [open, hasWindowLoader, idsKey, windowKey]);

    // BottomSheet이 visible/animation/backdrop/body overflow/드래그 닫기 처리.
    // focus, ESC 키 닫기만 자체 유지.
    useEffect(() => {
        if (open && timeDisplayRef.current) timeDisplayRef.current.focus();
    }, [open]);



    const windowReady = !hasWindowLoader || (windowState?.key === windowKey && !!windowState.data);
    const allowedWindow = hasWindowLoader && windowReady ? windowState?.data : null;
    const minDate = allowedWindow?.minDate || scheduledDate || today;
    const before = allowedWindow?.before ? new Date(allowedWindow.before) : null;
    const lastAllowed = before ? new Date(before.getTime() - 60000) : null;
    const maxDate = lastAllowed ? [today, localDate(lastAllowed)].sort()[0] : today;
    const canChooseDate = minDate < maxDate;
    const selectionError = (date, time, now = clock) => {
        if (!date || !isValidHHMM(time)) return localize('먹은 날짜와 시간을 선택해 주세요.');
        if (date < minDate) return localize("{v0} 이전 날짜로는 기록할 수 없어요.", { v0: dateLabel(minDate) });
        const at = new Date(`${date}T${time}:00`);
        if (Number.isNaN(at.getTime())) return localize('먹은 날짜와 시간을 선택해 주세요.');
        if (at > now) return localize('미래 시각은 기록할 수 없어요.');
        if (before && at >= before) return localize("{v0} {v1} 전까지만 기록할 수 있어요.", { v0: dateLabel(localDate(before)), v1: formatTime(`${pad2(before.getHours())}:${pad2(before.getMinutes())}`) });
        return '';
    };
    const error = selectionError(newDate, newTime);
    const valid = windowReady && !error && !submitting;
    // Keep shifted dates visible so an out-of-range day can be corrected.
    const showDateInput = canChooseDate || newDate !== today;
    const select = (date, time) => {
        if (!windowReady || selectionError(date, time, new Date())) return;
        setNewDate(date); setNewTime(time); setClock(new Date());
    };
    const shifted = (delta) => {
        const date = new Date(`${newDate}T${newTime}:00`);
        if (Number.isNaN(date.getTime())) return { date: '', time: '' };
        date.setMinutes(date.getMinutes() + delta);
        return { date: localDate(date), time: `${pad2(date.getHours())}:${pad2(date.getMinutes())}` };
    };
    const isBulk = mode === 'bulk' && targets.length > 1;
    const isRecord = action === 'record';
    const titleLabel = isRecord ? (isBulk ? localize('복용 한 번에 기록') : localize('복용 기록')) : (isBulk ? localize('복용 시간 한 번에 수정') : localize('복용 시간 수정'));
    const confirmLabel = isRecord ? (isBulk ? localize("{v0}개 모두 기록", { v0: targets.length }) : localize('기록하기')) : (isBulk ? localize("{v0}개 모두 변경", { v0: targets.length }) : localize('변경'));
    const handleSubmit = async () => {
        if (!valid || selectionError(newDate, newTime, new Date()) || submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        try {
            await onConfirm?.(newTime, newDate);
            onClose();
        } catch (_) {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    const chips = [
        { label: localize("예정 {v0}", { v0: formatTime(scheduledTime) }), flex: 2.2, date: scheduledDate || today, time: scheduledTime },
        { label: localize('지금'), flex: 0.7, date: today, time: nowHHMM(), useCurrentTime: true },
        { label: localize('−10분'), flex: 0.9, ...shifted(-10) },
        { label: localize('+10분'), flex: 0.9, ...shifted(10) },
    ];
    const yesterday = new Date(`${today}T12:00:00`);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateChoices = [{ label: localize('어제'), date: localDate(yesterday) }, { label: localize('오늘'), date: today }];
    const dateChoiceTime = (date) => {
        let time = newTime;
        if (date === today && time > nowHHMM()) time = nowHHMM();
        return time;
    };
    const timeLimits = ['23:59'];
    if (newDate === today) timeLimits.push(nowHHMM());
    if (lastAllowed && newDate === localDate(lastAllowed)) timeLimits.push(`${pad2(lastAllowed.getHours())}:${pad2(lastAllowed.getMinutes())}`);
    const timeMax = timeLimits.sort()[0];

    return (
        <BottomSheet
            dismissDisabled={submitting}
            open={open}
            onClose={onClose}
            ariaLabelledBy={titleId}
            borderRadius="22px 22px 0 0"
            handleColor="#E2DDD2"
            px="20px"
            footer={(
                <Box sx={{ display: 'flex', gap: '8px', fontFamily: FONT_STACK }}>
                    <Box component="button" type="button" onClick={onClose} disabled={submitting}
                        sx={{
                            flex: 1, minWidth: 0, minHeight: 48,
                            border: '1px solid #EDE8DD', bgcolor: '#fff', color: '#7A766F',
                            fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: '8px', borderRadius: '12px', cursor: 'pointer',
                            '&:active': { bgcolor: '#F8F5EE' },
                        }}>
                        <SingleLineText component="span" sx={{ display: 'block', fontSize: '1rem', fontWeight: 700 }}>{localize("취소")}</SingleLineText>
                    </Box>
                    <Box component="button" type="button" disabled={!valid} onClick={handleSubmit}
                        sx={{
                            flex: 2, minWidth: 0, minHeight: 48, border: 'none',
                            bgcolor: valid ? '#1F8A3D' : '#C7C0B3', color: '#fff',
                            fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: '8px', borderRadius: '12px',
                            cursor: valid ? 'pointer' : 'not-allowed',
                            boxShadow: valid ? '0 2px 8px rgba(31,138,61,0.25)' : 'none',
                            '&:active': valid ? { bgcolor: '#176F30' } : {},
                        }}>
                        {submitting ? <LoadingLabel /> : <SingleLineText component="span" sx={{ display: 'block', fontSize: '1.1rem', fontWeight: 700 }}>{confirmLabel}</SingleLineText>}
                    </Box>
                </Box>
            )}
        >
            <Box role="region" aria-labelledby={titleId} sx={{ fontFamily: FONT_STACK }}>
                <Box sx={{ mb: sheetSpacing.section }}>
                    <SingleLineText component="h2" id={titleId}
                        sx={{ fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.01em', color: '#1F1D1B', fontFamily: FONT_STACK }}>
                        {titleLabel}
                    </SingleLineText>
                    <Typography sx={{ mt: '4px', fontSize: '0.95rem', color: '#7A766F', lineHeight: 1.45, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
                        {isBulk ? localize("{v0}개 약에 함께 적용해요", { v0: targets.length }) : targets[0]?.name}
                    </Typography>
                </Box>

                <Box component="fieldset" disabled={submitting} sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
                    {canChooseDate && <Box sx={{ display: 'flex', gap: '6px', mb: '8px' }}>
                        {dateChoices.map(choice => <Box key={choice.label} component="button" type="button"
                            aria-pressed={newDate === choice.date}
                            disabled={!windowReady || choice.date < minDate || choice.date > maxDate}
                            onClick={() => { setNewDate(choice.date); setNewTime(dateChoiceTime(choice.date)); setClock(new Date()); }}
                            sx={{ minHeight: 44, px: '18px', borderRadius: 999, fontFamily: FONT_STACK, fontSize: '0.9rem', fontWeight: 700,
                                border: '1px solid #D4ECDA', bgcolor: newDate === choice.date ? '#E9F7ED' : '#fff', color: newDate === choice.date ? '#1F8A3D' : '#1F1D1B',
                                cursor: 'pointer', '&:disabled': { color: '#AAA59C', bgcolor: '#F8F7F4', cursor: 'not-allowed' } }}>
                            {choice.label}
                        </Box>)}
                    </Box>}
                    <Typography sx={{ mb: '8px', ml: '4px', fontSize: '0.9rem', fontWeight: 700, color: '#1F1D1B', lineHeight: 1.5 }}>
                        {newDate === today ? localize('오늘') : newDate === localDate(yesterday) ? localize('어제') : dateLabel(newDate)}
                        {newDate === today || newDate === localDate(yesterday) ? ` (${dateLabel(newDate)})` : ''}{localize(" 먹은 기록이에요.")}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: showDateInput ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)', gap: '8px', mb: '12px' }}>
                        {showDateInput && <DateInputCard id="taken-date" label={localize("실제 먹은 날짜")} tabIndex={0} minHeight={66}
                            valueFontSize={takenValueFontSize} labelColor={takenLabelColor} valueColor={takenValueColor} sx={{
                                py: 1, bgcolor: '#F1FBF3', borderColor: '#D4ECDA',
                                '&:active': { bgcolor: '#E9F7ED' },
                            }}
                            value={newDate} min={minDate} max={maxDate}
                            onChange={(e) => setNewDate(e.target.value)} />}
                        {/* The input receives the original gesture across the whole card. */}
                        <Box sx={{
                            position: 'relative', minWidth: 0, minHeight: 66,
                            bgcolor: '#F1FBF3', border: '1px solid #D4ECDA', borderRadius: 3,
                            py: 1, px: 1.25,
                            cursor: 'pointer',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                <SingleLineText sx={{ fontSize: '0.82rem', color: takenLabelColor, fontWeight: 800, lineHeight: 1.2 }}>{localize("실제 먹은 시간")}</SingleLineText>
                                <ChevronRightIcon sx={{ fontSize: 17, color: '#91AB98', flexShrink: 0 }} />
                            </Box>
                            <SingleLineText ref={timeDisplayRef} tabIndex={0} sx={{
                                mt: 0.7, fontSize: takenValueFontSize, fontWeight: 850, lineHeight: 1.4,
                                color: takenValueColor, fontVariantNumeric: 'tabular-nums', fontFamily: FONT_STACK,
                                outline: 'none', '&:focus-visible': { textDecoration: 'underline' },
                            }}>{formatTime(newTime)}</SingleLineText>
                            <Box component="input" type="time" value={newTime} max={timeMax}
                                onChange={(e) => { if (e.target.value) setNewTime(e.target.value); }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    try {
                                        if (typeof e.currentTarget.showPicker === 'function') {
                                            e.currentTarget.showPicker();
                                            return;
                                        }
                                    } catch (_) {}
                                    try { e.currentTarget.focus(); } catch (_) {}
                                }}
                                aria-label={localize("복용 시간 선택")}
                                sx={{
                                    position: 'absolute', inset: 0, width: '100%', height: '100%',
                                    zIndex: 1, opacity: 0, cursor: 'pointer',
                                    border: 'none', background: 'transparent', fontSize: 16, color: 'transparent',
                                    '&::-webkit-calendar-picker-indicator': { width: '100%', height: '100%', cursor: 'pointer' },
                                }} />
                        </Box>
                    </Box>
                    <Box sx={{ display: readAppLanguage() === 'ko' ? 'flex' : 'grid', ...(readAppLanguage() !== 'ko' && { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }), gap: '6px', flexWrap: 'nowrap' }}>
                        {chips.map((c, index) => (
                            <Box key={c.label} component="button" type="button" onClick={() => select(c.useCurrentTime ? localDate() : c.date, c.useCurrentTime ? nowHHMM() : c.time)}
                                disabled={submitting || !windowReady || !!selectionError(c.date, c.time)}
                                title={selectionError(c.date, c.time) || undefined}
                                sx={{
                                    flex: `${c.flex} 1 0`, minWidth: 0, minHeight: 44,
                                    ...(readAppLanguage() !== 'ko' && index === 0 && { gridColumn: '1 / -1' }),
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid #EDE8DD', bgcolor: '#fff', color: '#1F1D1B',
                                    fontFamily: FONT_STACK, py: '8px', px: '8px', borderRadius: 999, cursor: 'pointer',
                                    fontVariantNumeric: 'tabular-nums', '&:active': { bgcolor: '#F8F5EE' },
                                    '&:disabled': { color: '#AAA59C', bgcolor: '#F8F7F4', cursor: 'not-allowed' },
                                }}>
                                <SingleLineText component="span" sx={{ display: 'block', fontSize: '0.9rem', fontWeight: 600 }}>{c.label}</SingleLineText>
                            </Box>
                        ))}
                    </Box>
                </Box>
                {(scheduledDate < today || (before && !canChooseDate)) && <Typography sx={{ mt: '12px', ml: '4px', fontSize: '0.9rem', fontWeight: 700, color: '#1F1D1B', lineHeight: 1.5 }}>
                    {canChooseDate ? localize('자정이 지났다면 먹은 날짜도 바꿔주세요.') : before ? localize("이 회차는 {v0}에 먹은 시간만 기록할 수 있어요.", { v0: dateLabel(minDate) }) : null}
                </Typography>}
                {before && canChooseDate && <Typography sx={{ mt: '8px', ml: '4px', fontSize: '0.85rem', color: '#7A766F', lineHeight: 1.5 }}>
                    {dateLabel(localDate(before))} {formatTime(`${pad2(before.getHours())}:${pad2(before.getMinutes())}`)}{localize(" 전까지 먹은 기록을 남길 수 있어요.")}</Typography>}
                {!windowReady && <Box sx={{ mt: '12px', ml: '4px' }}>
                    {windowState?.key === windowKey && windowState.error ? <>
                        <Typography role="alert" sx={{ fontSize: '0.9rem', color: '#c62828' }}>{localize("기록 가능한 시간을 확인하지 못했어요.")}</Typography>
                        <Box component="button" type="button" onClick={() => setRefresh(value => value + 1)} sx={{ minHeight: 44, px: 1.5, mt: 1, border: '1px solid #EDE8DD', borderRadius: 2, bgcolor: '#fff', fontFamily: FONT_STACK }}>{localize("다시 확인")}</Box>
                    </> : <Typography role="status" sx={{ fontSize: '0.9rem', color: '#7A766F' }}>{localize("기록 가능한 시간을 확인하고 있어요.")}</Typography>}
                </Box>}
                {error && <Typography role="alert" sx={{ color: '#c62828', fontSize: '0.9rem', mt: 1, ml: '4px', lineHeight: 1.5 }}>{error}</Typography>}

            </Box>
        </BottomSheet>
    );
}
